import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { FormulaireCoordonneesMouvementTelephoneModel } from '../../models/formulaire/formulaire-coordonnees-mouvement-telephone.model';

import { GlobalStoreService } from '@services/global-store/global-store.service';
import { FormulaireMouvementService } from '@services/formulaire-mouvement/formulaire-mouvement.service';
import { GabaritService } from '@services/gabarit/gabarit.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { FormulaireCoordonneesMouvementTelephoneBuilder } from '../../models/formulaire/formulaire-coordonnees-mouvement-telephone-builder.model';
import { ParticulierMouvement } from '../../api/model/particulier-mouvement.model';
import { GestionTitreService } from '@services/navigation/gestion-titre.service';
import { AccessibiliteService } from '@services/accessibilite.service';
import { TelephoneTypeEnum } from 'src/app/constantes/telephone-type.enum';
import { TelephoneTypeLieuEnum } from 'src/app/constantes/telephone-type-lieu.enum';
import { ContexteModificationEnum } from '../../constantes/contexte-modification.enum';
import { CybermetrieCoordonneesService } from '@services/cybermetrie/cybermetrie.service';
import { DsdFormSubmitEvent } from '@dsd/library/dist/types/components/form';
import { DsdFormSummaryError } from '@dsd/library/dist/types/components/form-error-summary/form-error-summary.model';
import { CybermetrieEnum } from '../../constantes/cybermetrie.enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-modifier-telephone',
  templateUrl: './modifier-telephone.component.html',
  standalone: false,
})
export class ModifierTelephoneComponent implements OnInit, AfterViewInit {
  isAppMobile?: boolean;
  coordonneesParticulierMouvement: ParticulierMouvement | null = null;
  formGroup!: FormGroup<FormulaireCoordonneesMouvementTelephoneModel>;

  @ViewChild('numeroTelephone', { static: true }) telephoneInput!: HTMLDsdInputElement;

  private readonly globalStoreService = inject(GlobalStoreService);
  private readonly formulaireMouvementService = inject(FormulaireMouvementService);
  private readonly gabaritService = inject(GabaritService);
  private readonly navigationService = inject(NavigationService);
  private readonly gestionTitreService = inject(GestionTitreService);
  private readonly accessibiliteService = inject(AccessibiliteService);
  private readonly cybermetrieCoordonneesService = inject(CybermetrieCoordonneesService);
  private readonly translateService = inject(TranslateService);

  ngOnInit(): void {
    this.isAppMobile = this.gabaritService.isAppMobile();
    const formExistant = this.formulaireMouvementService.getFormTelephone();
    if (formExistant?.get('numeroTelephone')?.value) {
      this.formGroup = formExistant;
      this.setTitreOnglet();
    } else {
      this.globalStoreService.getCoordonneesMouvement$().subscribe({
        next: (data) => {
          this.coordonneesParticulierMouvement = data;
          this.formGroup = FormulaireCoordonneesMouvementTelephoneBuilder.buildTelephone(
            data?.telephones?.find(
              (tel) => tel.typeLieu === TelephoneTypeLieuEnum.RESIDENCE_PRINCIPALE,
            ) ?? null,
          );
          this.formulaireMouvementService.setFormTelephone(this.formGroup);
          this.setTitreOnglet();
          this.cybermetrieCoordonneesService.transactionDebuter(
            this.formGroup.getRawValue().telephoneExistant
              ? CybermetrieEnum.LABEL_MODIFIER_TELEPHONE
              : CybermetrieEnum.LABEL_MODIFIER_TELEPHONE_AJOUT,
          );
        },
      });
    }
    this.globalStoreService.saveContexteModification(ContexteModificationEnum.TELEPHONE);
  }

  setTitreOnglet(): void {
    if (this.estTelephoneExistant) {
      this.gestionTitreService.addTitreOnglet('titreOnglet.modifierTelephone');
    } else {
      this.gestionTitreService.addTitreOnglet('titreOnglet.ajouterTelephone');
    }
  }

  ngAfterViewInit(): void {
    this.accessibiliteService.setFocus('#titreTelephone');
    this.telephoneInput.registerCustomValidate(
      (value: string) => {
        return this.validerInput(value, 'numeroTelephone', 'telephoneNombreNonRepete');
      },
      { errorKey: 'telephoneNombreNonRepete' },
    );
  }

  get estTelephoneExistant(): boolean {
    return this.formGroup.getRawValue().telephoneExistant;
  }

  validerInput(value: string, nomFormControl: string, codeErreur: string) {
    const control = this.formGroup.get(nomFormControl);
    if (!control) return false;
    control.setValue(value);
    return !control.hasError(codeErreur);
  }

  public typeAppareil(value: TelephoneTypeEnum): void {
    this.formGroup?.patchValue({
      typeAppareil: value,
      numeroPoste: value === TelephoneTypeEnum.CELLULAIRE ? null : this.formGroup.value.numeroPoste, // Efface le poste si le type est CELLULAIRE
    });
  }

  public estTelephoneFixe(): boolean {
    return this.formGroup?.value.typeAppareil === TelephoneTypeEnum.TELEPHONE;
  }

  public get telephoneTypeEnum() {
    return TelephoneTypeEnum;
  }

  naviguerVersValidation(event: CustomEvent<DsdFormSubmitEvent>): void {
    if (this.formGroup?.valid) {
      this.formulaireMouvementService.setFormTelephone(this.formGroup);
      this.navigationService.naviguerVersValidationTelephone();
    } else {
      this.personaliserMessageErreur(event);
    }
  }

  private personaliserMessageErreur(event: CustomEvent<DsdFormSubmitEvent>): void {
    const listIdentifiers = new Set(['postePrincipal']);
    const errors = event.detail.errors;
    if (errors) {
      const labelToModify = (errors as DsdFormSummaryError[]).find((el: DsdFormSummaryError) =>
        listIdentifiers.has(el['id']),
      );
      if (labelToModify) {
        labelToModify['text'] = this.translateService.instant('modifierTelephones.horsEcran.poste');
      }
      this.cybermetrieErreurs(errors as DsdFormSummaryError[]);
    }
  }

  private cybermetrieErreurs(errors: DsdFormSummaryError[]): void {
    let texteErreurs: string[] = [];
    errors.forEach((erreurForm) => {
      texteErreurs.push(erreurForm.text ?? '');
    });
    this.cybermetrieCoordonneesService.transactionErreurUtilisateur(
      CybermetrieEnum.LABEL_MODIFIER_TELEPHONE,
      texteErreurs,
    );
  }

  annuler(): void {
    this.globalStoreService.saveEtatAfficherModaleAnnuler(true);
  }
}
